import { inlineAttributesVariables, inlineCssStyles } from '@/utils/css/css';
import { downloadImage } from '@/utils/canvas/canvas';
import { isSafari } from '@/utils/browser';

/**
 * Prepare an exportable clone of the given svg element, the clone will be
 * inserted in the DOM, so we can inline variable values and styles.
 * @param originalSvg
 * @returns {
 *   svg -  the cloned svg element
 *   container - the container element that contains the cloned svg
 *   cleanup - the cleanup function to remove the container and clone
 * }
 */
const getExportableSvgClone = (originalSvg: SVGSVGElement, ratio = 2) => {
  const svg = originalSvg.cloneNode(true) as SVGSVGElement;
  const container = document.createElement('div');
  container.setAttribute(
    'style',
    'position: absolute; height: 1px; width: 1px; opacity: 0; overflow: hidden'
  );
  originalSvg.insertAdjacentElement('afterend', container);
  svg.setAttribute('width', (Number(svg.getAttribute('width')) * ratio).toString());
  svg.setAttribute('height', (Number(svg.getAttribute('height')) * ratio).toString());
  container.appendChild(svg);

  // Inline styles and variables values
  inlineCssStyles(svg);
  inlineAttributesVariables(svg, 'fill');
  inlineAttributesVariables(svg, 'stroke');
  inlineAttributesVariables(svg, 'stop-color');

  const cleanup = () => {
    container.remove();
  };

  return { svg, container, cleanup };
};

/**
 * Export the given svg element to a branded png image
 * @param originalSvg - the svg to export
 * @param filename - the filename for the exported image
 * @param config - the configuration object
 */
export const exportSvgToPng = (
  originalSvg: SVGSVGElement,
  filename = 'finary',
  config: {
    padding?: number;
    addLogo?: boolean;
    logoGap?: number;
    backgroundColor?: string;
    ratio?: number;
  } = {}
) => {
  const {
    padding = 16,
    addLogo = true,
    logoGap = 16,
    backgroundColor = '#000',
    ratio = 2,
  } = config;
  const LOGO_DATA = `<svg width="132" height="36" viewBox="0 0 88 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M29.733 0C30.1406 0 30.4606 0.00260287 30.8003 0.0535714V2.52553C30.4946 2.49155 30.2765 2.49744 30.0387 2.49744C29.3084 2.49744 28.714 2.71831 28.2554 3.16003C27.8139 3.60175 27.5931 4.20488 27.5931 4.9694V6.42857H30.8003V8.95362H27.5931V19.9286H24.7398V8.95362H22.3223V6.42857H24.7398V5.04585C24.7398 3.48283 25.1898 2.2511 26.09 1.35066C26.9901 0.450219 28.2045 0 29.733 0Z" fill="#F1C086"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M32.0879 2.35712V5.14283H34.8781V2.35712H32.0879ZM32.0879 19.9285V6.42854H34.8781V19.9285H32.0879Z" fill="#F1C086"></path><path d="M43.5785 6.21429C40.6341 6.21429 39.2395 8.49138 38.9554 9.26766V6.42858H36.166V19.9286H38.9554V11.8811C39.0329 11.1825 39.4203 10.458 40.0659 9.75931C40.7375 9.03478 41.5898 8.67251 42.6229 8.67251C44.4824 8.67251 45.8254 9.88869 45.8254 12.2952V19.9286H48.6148V12.1399C48.6148 10.1992 48.1241 8.72427 47.1426 7.7151C46.1612 6.70594 44.9731 6.21429 43.5785 6.21429Z" fill="#F1C086"></path><path d="M56.4052 19.9286C58.4972 19.9286 60.1607 18.7603 60.7656 17.5159H60.9168V19.9286H63.6389V6.42858H60.9168V8.62699H60.7656C60.1607 7.38255 58.4972 6.21429 56.4052 6.21429C54.8929 6.21429 53.3302 6.79842 52.0447 7.89048C50.7593 8.98255 49.9023 10.8619 49.9023 13.0714C49.9023 15.281 50.7593 17.1603 52.0447 18.2524C53.3302 19.3445 54.8929 19.9286 56.4052 19.9286ZM56.758 17.4905C55.649 17.4905 54.666 17.1095 53.8091 16.3222C52.9773 15.5349 52.5488 14.4683 52.5488 13.0714C52.5488 11.6746 52.9773 10.6079 53.8091 9.82064C54.666 9.03334 55.649 8.65239 56.758 8.65239C57.8922 8.65239 58.8752 9.03334 59.707 9.82064C60.5639 10.6079 60.9924 11.6746 60.9924 13.0714C60.9924 14.4683 60.5639 15.5349 59.707 16.3222C58.8752 17.1095 57.8922 17.4905 56.758 17.4905Z" fill="#F1C086"></path><path d="M72.5996 6.21429C70.181 6.21429 68.3346 7.77274 67.7364 9.87663V6.42209H64.9277V19.9286H67.7364V13.5649C67.8924 12.1623 68.4386 11.0455 69.3488 10.1883C70.259 9.33118 71.3773 8.88962 72.6776 8.88962C72.9897 8.88962 73.2237 8.91559 73.4058 8.94157V6.26624C73.2237 6.24027 72.9637 6.21429 72.5996 6.21429Z" fill="#F1C086"></path><path d="M81.132 15.9088L77.036 6.21429H74.2637L79.7833 18.8897L77.4606 24H80.1579L82.3308 19.2404L88.0003 6.21429H85.228L81.132 15.9088Z" fill="#F1C086"></path></g><g><path fill-rule="evenodd" clip-rule="evenodd" d="M6 6.42859C2.68629 6.42859 0 9.11488 0 12.4286H12.0293C15.343 12.4286 18.0293 9.7423 18.0293 6.42859H6ZM6 13.9286C2.68629 13.9286 0 16.6149 0 19.9286H5.59024C8.90395 19.9286 11.5902 17.2423 11.5902 13.9286H6Z" fill="#F1C086"></path></g></svg>`;
  const LOGO_WIDTH = 66 * ratio;
  const LOGO_HEIGHT = 18 * ratio;
  // In safari, we don't scale the svg as this causes with the foreign objects being misplaced,
  // instead, we just draw it bigger on the canvas, we keep scaling the svg for other browsers
  const { svg, cleanup } = getExportableSvgClone(originalSvg, isSafari() ? 1 : 2);

  // convert to png
  const svgData = new XMLSerializer().serializeToString(svg);
  const svgDataBase64 = btoa(unescape(encodeURIComponent(svgData)));
  const svgDataUrl = `data:image/svg+xml;charset=utf-8;base64,${svgDataBase64}`;
  const logoDataBase64 = btoa(unescape(encodeURIComponent(LOGO_DATA)));
  const logoDataUrl = `data:image/svg+xml;charset=utf-8;base64,${logoDataBase64}`;
  const width = Number(svg.getAttribute('width')) * ratio;
  const height = Number(svg.getAttribute('height')) * ratio;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = width + padding * 2 * ratio;
  canvas.height = height + ((addLogo ? LOGO_HEIGHT : 0) + logoGap + padding * 2) * ratio;
  console.log(context);

  if (context) {
    const image = new Image();
    image.style.width = `${width}px`;
    image.style.height = `${height}px`;

    // Draw the chart, within a rounded rect clip path
    const drawMainImage = () => {
      context.roundRect(
        0,
        0,
        width + padding * 2 * ratio,
        height + LOGO_HEIGHT + (logoGap + padding * 2) * ratio,
        16
      );
      context.fill();
      context.save();
      // clip image with a rounded rect
      context.beginPath();
      context.roundRect(padding * ratio, padding * ratio, width, height, 8);
      context.clip();
      context.drawImage(image, padding * ratio, padding * ratio, width, height);
      context.restore();
    };

    // Draw the logo
    const drawLogo = () => {
      context.drawImage(
        image,
        padding * ratio + (width - LOGO_WIDTH) / 2,
        padding * ratio + height + logoGap * ratio,
        LOGO_WIDTH,
        LOGO_HEIGHT
      );
    };

    const finish = () => {
      downloadImage(canvas, filename);
      cleanup();
    };

    // Draw the main chart once the image has first loaded
    image.addEventListener(
      'load',
      () => {
        context.fillStyle = backgroundColor;
        drawMainImage();

        if (addLogo) {
          // On next image load, draw the logo
          image.addEventListener(
            'load',
            () => {
              drawLogo();
              finish();
            },
            { once: true }
          );

          // Load the logo
          image.style.width = `${LOGO_WIDTH}px`;
          image.style.height = `${LOGO_HEIGHT}px`;
          image.src = logoDataUrl;
        } else {
          finish();
        }
      },
      { once: true }
    );

    // Load the main image
    image.src = svgDataUrl;
  }
};
